import React from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useTranslation } from 'react-i18next';
import { theme as salooneTheme } from '../../../theme';

const { colors } = salooneTheme;

const PermissionMatrix = ({ groups = [], value, onChange, canGrantManagerManage = false }) => {
  const { t } = useTranslation();
  const translate = (key, fallback) => {
    const translated = t(key, { defaultValue: fallback });
    return typeof translated === 'string' ? translated : fallback;
  };
  const permissionCodes = value?.permission_codes || [];
  const managerManage = groups
    .flatMap((group) => group.permissions || [])
    .find((permission) => permission.code === 'managers.manage');

  const toggleCode = (code) => {
    const nextCodes = permissionCodes.includes(code)
      ? permissionCodes.filter((item) => item !== code)
      : [...permissionCodes, code];
    onChange({ ...value, permission_codes: nextCodes.sort() });
  };

  return (
    <Box>
      <Paper
        variant="outlined"
        sx={{
          px: { xs: 2, sm: 2.5 },
          py: 2,
          mb: 2,
          borderColor: colors.primary[200],
          background: `linear-gradient(135deg, ${colors.primary[50]}, ${colors.secondary[50]})`,
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Typography sx={{ color: colors.primary[900], fontWeight: 700 }}>
              {translate('pro_manager_access.full_access', 'Gestion totale')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {translate(
                'pro_manager_access.full_access_help',
                'Donne tous les droits de travail courants. La gestion des gestionnaires reste séparée.',
              )}
            </Typography>
          </Box>
          <Switch
            checked={Boolean(value?.is_full_access)}
            onChange={(event) => onChange({ ...value, is_full_access: event.target.checked })}
            inputProps={{ 'aria-label': translate('pro_manager_access.full_access', 'Gestion totale') }}
          />
        </Box>
      </Paper>

      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ borderColor: colors.primary[200], borderRadius: 2, overflowX: 'auto' }}
      >
        <Table size="small" aria-label={translate('pro_manager_access.permissions_matrix', 'Matrice des droits')} sx={{ minWidth: 640 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: colors.primary[100] }}>
              <TableCell sx={{ width: '30%', color: colors.primary[900], fontWeight: 800 }}>
                {translate('pro_manager_access.matrix.feature', 'Fonctionnalité')}
              </TableCell>
              <TableCell sx={{ color: colors.primary[900], fontWeight: 800 }}>
                {translate('pro_manager_access.matrix.permissions', 'Permissions')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.map((group) => {
              const operationalPermissions = (group.permissions || []).filter(
                (permission) => permission.code !== 'managers.manage',
              );
              if (!operationalPermissions.length) return null;
              return (
                <TableRow key={group.code} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{
                      verticalAlign: 'top',
                      backgroundColor: colors.secondary[50],
                      color: colors.primary[900],
                      fontWeight: 700,
                      py: 2,
                    }}
                  >
                    {group.label || translate(group.label_key, group.code)}
                  </TableCell>
                  <TableCell sx={{ py: 1.25 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {operationalPermissions.map((permission) => {
                        const checked = permissionCodes.includes(permission.code);
                        return (
                          <FormControlLabel
                            key={permission.code}
                            control={(
                              <Checkbox
                                checked={checked}
                                disabled={Boolean(value?.is_full_access)}
                                onChange={() => toggleCode(permission.code)}
                                size="small"
                              />
                            )}
                            label={permission.label || translate(permission.label_key, permission.code)}
                            sx={{
                              m: 0,
                              pr: 1.25,
                              minHeight: 38,
                              border: '1px solid',
                              borderColor: checked ? colors.primary[300] : colors.grey[300],
                              borderRadius: 1.5,
                              backgroundColor: checked ? colors.primary[50] : colors.background.primary,
                              '& .MuiFormControlLabel-label': { fontSize: '0.875rem' },
                            }}
                          />
                        );
                      })}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {canGrantManagerManage && managerManage && (
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mt: 2,
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            gap: 1,
            borderColor: colors.secondary[300],
            backgroundColor: colors.secondary[50],
            borderRadius: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LockOutlinedIcon sx={{ color: colors.secondary[700], fontSize: 20 }} />
            <Typography variant="subtitle2" sx={{ color: colors.secondary[900] }}>
              {translate('pro_manager_access.special_permission', 'Droit spécial réservé à la responsable')}
            </Typography>
          </Box>
          <FormControlLabel
            sx={{ m: 0 }}
            control={(
              <Checkbox
                checked={permissionCodes.includes('managers.manage')}
                onChange={() => toggleCode('managers.manage')}
              />
            )}
            label={managerManage.label || translate(
              managerManage.label_key,
              'Autoriser à gérer les gestionnaires',
            )}
          />
        </Paper>
      )}
    </Box>
  );
};

export default PermissionMatrix;
